import 'firebase/compat/firestore';

import { ArtistId } from "../models/Artist";
import { Group, GroupId } from "../models/Group";
import { OwnerId } from "../models/User";
import { uuidv4 } from "../util/sugar";

import firebase from "firebase/compat/app";
import 'firebase/compat/firestore';
const firestore = firebase.firestore


export class GroupService {
  static getUserGroups =  (
    userId: string,
    field: "members" | "ownerId",
    onSuccess: (groups: Group[]) => void
  ) => {
    const db = firestore();
    const comparator = field === "ownerId" ? "==" : "array-contains";

    return new Promise((resolve) => {
      db.collection("groups")
        .where(field, comparator, userId)
        .onSnapshot((groupDoc) => {
          const userGroups = [...groupDoc.docs].map((x) =>
            GroupService.StronglyTypeGroupData(x)
          );
          onSuccess(userGroups || []);
          resolve(userGroups);
        });
    });
  };

  static observe = (
    ownerId: OwnerId,
    artistId: ArtistId,
    onGroupAccessGranted: (groups: Group[]) => void
  ) => {
    return Promise.all([GroupService.getUserGroups(artistId, "members", onGroupAccessGranted),
      GroupService.getUserGroups(ownerId, "ownerId", onGroupAccessGranted)])
  };

  static changeGroup = (groupId: GroupId, update: any) => {
    return new Promise<Group>((resolve, reject) => {
      const db = firestore();
      console.trace();
      const groupRef = db.collection("groups").doc(groupId);
      groupRef
        .set(update, { merge: true })
        .then(() => {
          groupRef
            .get()
            .then((groupDoc) => {
              return resolve(groupDoc.data() as Group);
            })
            .catch(reject);
        })
        .catch(reject);
    });
  };

  static allGroups = () => {
    return new Promise<Group[]>((resolve, reject) => {
      const db = firestore();
      const groupsRef = db.collection("groups");
      groupsRef.get()
        .then((items) => {
          const myGroups=items.docs.map(x => ({ id: x.id, ...x.data() }) as any as Group) as Group[];
          return resolve(myGroups);
        })
        .catch(reject);
    });
  };
  static fetchGroup = (groupId: GroupId) => {
    return new Promise<Group>((resolve, reject) => {
      const db = firestore();
      const groupsRef = db.collection("groups").doc(groupId);
      groupsRef.get()
        .then((doc) => {
          console.log("FETCHED", doc.data());
          return resolve(({ ...doc.data(), id: doc.id } as Group));
        })
        .catch(reject);
    });
  };


  /**
   * Clone a group for a new run of the same course: copies its assignments
   * (the whole `assignment/{groupId}` doc, keyed by assignmentId) and its
   * invite code (`invites/{groupId}`'s `code` field) onto a fresh group doc,
   * so an admin reusing a course doesn't have to re-enter every assignment
   * by hand.
   *
   * Deliberately NOT copied: `members`/`invites` (the new group starts with
   * an empty roster — the old roster belonged to the prior run, not this
   * one) and the invite LINK (it embeds the group id directly, e.g.
   * `/invitation/?session-id=<groupId>`, so a new group id already makes it
   * different — nothing to do there). The invite CODE is reused as-is per
   * the caller's explicit ask; only the doc it's copied into changes.
   */
  static duplicateGroup = async (
    sourceGroupId: GroupId,
    newGroupId: GroupId,
    newTitle: string,
    authorId: string,
    ownerId: OwnerId
  ): Promise<void> => {
    const db = firestore();

    const sourceDoc = await db.collection("groups").doc(sourceGroupId).get();
    const source = sourceDoc.data() as Group | undefined;

    const newGroup: Record<string, any> = {
      id: newGroupId,
      title: newTitle,
      description: source?.description || "",
      active: true,
      members: [authorId],
      invites: [],
      path: "",
      ownerId,
      authorId,
      createdAt: firestore.FieldValue.serverTimestamp(),
    };
    if (source?.defaultPrivacy) newGroup.defaultPrivacy = source.defaultPrivacy;

    await db.collection("groups").doc(newGroupId).set(newGroup);

    const assignmentsDoc = await db.collection("assignment").doc(sourceGroupId).get();
    const sourceAssignments = assignmentsDoc.data() || {};
    if (Object.keys(sourceAssignments).length > 0) {
      // Fresh ids, not the source's: fetchSongsForAssignment() queries songs
      // by `assignment == assignmentId` alone whenever it's called without a
      // groupId (e.g. TeacherAdmin's per-group song counts) — reusing the
      // source's ids would make a new group's (as-yet-empty) assignment
      // silently merge song counts with the source group's submissions the
      // moment anyone submitted to it, since both groups' assignment docs
      // would reference the exact same id.
      const newAssignments: Record<string, any> = {};
      Object.values(sourceAssignments).forEach((assignment: any) => {
        const newAssignmentId = uuidv4();
        newAssignments[newAssignmentId] = { ...assignment, id: newAssignmentId };
      });
      await db.collection("assignment").doc(newGroupId).set(newAssignments);
    }

    const inviteDoc = await db.collection("invites").doc(sourceGroupId).get();
    const code = inviteDoc.exists ? (inviteDoc.data() || {}).code : undefined;
    if (code) {
      await db.collection("invites").doc(newGroupId).set({ code, groupId: newGroupId });
    }
  };

  static deleteGroup = (groupId: GroupId) => {
    return new Promise<Group>((resolve, reject) => {
      const db = firestore();
      const groupRef = db.collection("groups").doc(groupId);
      groupRef
        .delete()
        .then(() => {
          resolve(undefined as any);
        })
        .catch(reject);
    });
  };

  static StronglyTypeGroupData = (
    groupDoc:
      | firebase.firestore.QueryDocumentSnapshot<firebase.firestore.DocumentData>
      | firebase.firestore.DocumentSnapshot<firebase.firestore.DocumentData>
  ) => {
    return { ...groupDoc.data(), id: groupDoc.id } as Group;
  };
}
